import {
  addressSchema,
  agendaSlotTypeSchema,
  doctorSubstituteDocumentApiSchema,
  frenchDepartmentCodeSchema,
  GetDoctorSubstituteApiParams,
  pictureSchema,
  signatureUrlSchema,
} from '@omecare/utils';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import http from '@/utils/http.service';

const getDoctorSubstituteAgendaItemResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  accessType: agendaSlotTypeSchema,
});

export const getDoctorSubtituteResponseSchema = z.object({
  id: z.string().uuid(),
  firstname: z.string(),
  lastname: z.string(),
  email: z.string(),
  phoneNumber: z.string().nullable().default(null),
  picture: pictureSchema,
  address: addressSchema,
  orderRegistrationNumber: z.string(),
  rppsNumber: z.string(),
  dateOfBirth: z.coerce.date(),
  thesis: z.boolean(),
  installable: z.boolean(),
  rhythm: z.string().nullable().default(null),
  // Structured rhythm fields
  onSiteSlotsWithoutAssistant: z.number().int().nullable().default(null),
  remoteSlotsWithoutAssistant: z.number().int().nullable().default(null),
  onSiteSlotsWithAssistant: z.number().int().nullable().default(null),
  remoteSlotsWithAssistant: z.number().int().nullable().default(null),
  rhythmNote: z.string().nullable().default(null),
  doctolibId: z.string().nullable().default(null),
  priority: z.number().int().nullable().optional(),
  iban: z.string(),
  bic: z.string().nullable().default(null),
  signatureUrl: signatureUrlSchema,
  // Contract profile (issue #796)
  urssafNumber: z.string().nullable().default(null),
  licenceNumber: z.string().nullable().default(null),
  licenceExpiresAt: z.coerce.date().nullable().default(null),
  licenceCouncilDepartmentCode: frenchDepartmentCodeSchema
    .nullable()
    .default(null),
  licenceDocument: doctorSubstituteDocumentApiSchema,
  rcpDocument: doctorSubstituteDocumentApiSchema,
  profileComplete: z.boolean().default(false),
  createdAt: z.coerce.date(),
  siteAccesses: z
    .array(getDoctorSubstituteAgendaItemResponseSchema)
    .optional()
    .default([]),
});

export type GetDoctorSubstituteResponse = z.infer<
  typeof getDoctorSubtituteResponseSchema
>;

const getDoctorSubstitute =
  (params: GetDoctorSubstituteApiParams) => async () => {
    return http.doctorSubstitutes
      .getDetails(params)
      .then<GetDoctorSubstituteResponse>(({ response, transform }) => {
        const result: any = transform.handle({
          response,
          attributes: {
            firstname: true,
            lastname: true,
            email: true,
            phoneNumber: true,
            picture: true,
            address: true,
            orderRegistrationNumber: true,
            rppsNumber: true,
            signatureUrl: true,
            dateOfBirth: true,
            thesis: true,
            installable: true,
            rhythm: true,
            onSiteSlotsWithoutAssistant: true,
            remoteSlotsWithoutAssistant: true,
            onSiteSlotsWithAssistant: true,
            remoteSlotsWithAssistant: true,
            rhythmNote: true,
            doctolibId: true,
            priority: true,
            iban: true,
            bic: true,
            urssafNumber: true,
            licenceNumber: true,
            licenceExpiresAt: true,
            licenceCouncilDepartmentCode: true,
            licenceDocument: true,
            rcpDocument: true,
            profileComplete: true,
            createdAt: true,
          },
          relations: {
            siteAccesses: true,
          },
          schema: getDoctorSubtituteResponseSchema,
        });

        return {
          ...result,
          siteAccesses: result.siteAccesses!,
        };
      });
  };

export const useGetDoctorSubstituteQuery = (
  params: GetDoctorSubstituteApiParams,
): UseQueryResult<GetDoctorSubstituteResponse, Error> => {
  return useQuery({
    queryFn: getDoctorSubstitute(params),
    queryKey: ['doctor-substitute', params.id],
    retry: false,
  });
};
